import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'

interface AnnouncementPaginationProps {
  page: number
  totalPages: number
  onChange: (page: number) => void
  windowSize?: number
}

const DEFAULT_WINDOW = 5

export function AnnouncementPagination({
  page,
  totalPages,
  onChange,
  windowSize = DEFAULT_WINDOW,
}: AnnouncementPaginationProps) {
  const pages = useMemo(() => {
    if (totalPages <= 0) return []
    const half = Math.floor(windowSize / 2)
    let start = Math.max(0, page - half)
    const end = Math.min(totalPages, start + windowSize)
    start = Math.max(0, end - windowSize)
    return Array.from({ length: end - start }, (_, i) => start + i)
  }, [page, totalPages, windowSize])

  if (totalPages <= 1) return null

  const canPrev = page > 0
  const canNext = page < totalPages - 1

  const arrowBase =
    'grid place-items-center rounded text-[#494949] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:text-[#a0a0a0] size-8 lg:size-10'

  return (
    <nav
      className="flex items-center justify-between mt-10 px-6 lg:px-0 lg:justify-center lg:gap-10"
      aria-label="pagination"
    >
      <div className="flex items-center gap-1 lg:gap-4">
        <button
          type="button"
          onClick={() => canPrev && onChange(0)}
          disabled={!canPrev}
          className={arrowBase}
          aria-label="first page"
        >
          <ChevronsLeft className="size-5 lg:size-6" />
        </button>
        <button
          type="button"
          onClick={() => canPrev && onChange(page - 1)}
          disabled={!canPrev}
          className={arrowBase}
          aria-label="previous page"
        >
          <ChevronLeft className="size-5 lg:size-6" />
        </button>
      </div>

      <div className="flex items-center gap-1 lg:gap-4">
        {pages.map((p) => {
          const active = p === page
          return (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'grid place-items-center font-semibold tracking-[-0.6px]',
                'size-8 text-sm lg:size-10 lg:text-base',
                active
                  ? 'rounded-lg bg-[#f4f4f4] text-[#4599ff]'
                  : 'rounded text-[#131313] hover:bg-gray-50',
              )}
            >
              {p + 1}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-1 lg:gap-4">
        <button
          type="button"
          onClick={() => canNext && onChange(page + 1)}
          disabled={!canNext}
          className={arrowBase}
          aria-label="next page"
        >
          <ChevronRight className="size-5 lg:size-6" />
        </button>
        <button
          type="button"
          onClick={() => canNext && onChange(totalPages - 1)}
          disabled={!canNext}
          className={arrowBase}
          aria-label="last page"
        >
          <ChevronsRight className="size-5 lg:size-6" />
        </button>
      </div>
    </nav>
  )
}
